import { useEffect, useState } from "react";
import { Calculator, X, BarChart3, RefreshCcw } from "lucide-react";
import toast from "react-hot-toast";
import paymentMethodsService from "../../Services/paymentMethods";
import appAnalyticsService from "../../Services/appAnalyticsService";

export default function InvoicesForm({
  form,
  onChange,
  onSubmit,
  onCancel,
  isEditing,
  publishers = [],
  apps = [],
}) {
  const [isFetchingAnalytics, setIsFetchingAnalytics] = useState(false);
  // inside component
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPayment, setSelectedPayment] = useState(null);


  // Fetch payment methods when publisher changes
  useEffect(() => {
    if (!form.publisherId) return;
    paymentMethodsService
      .getByPublisher(form.publisherId)
      .then((res) => {
        // const active = res.filter((m) => m.isActive && m.verified);
        const active = res.filter((m) => m.verified);
        setPaymentMethods(active);
      })
      .catch(() => toast.error("Failed to load payment methods"));
  }, [form.publisherId]);


  // Auto calculations
  useEffect(() => {
    const original = parseFloat(form.originalRevenue || 0);
    const deduction = parseFloat(form.deduction || 0);
    const finalRevenue = original - deduction;

    const deductionPercentage = parseFloat(form.deductionPercentage || 0);
    const deductedAmount = ((finalRevenue * deductionPercentage) / 100).toFixed(2);
    const publisherRevenue = (finalRevenue - deductedAmount).toFixed(2);

    onChange({
      ...form,
      finalRevenue,
      deductedAmount,
      publisherRevenue,
    });
  }, [form.originalRevenue, form.deduction, form.deductionPercentage]);

  // Handle selecting / deselecting apps or websites
  const handleAppSelection = (appId) => {
    const selected = form.selectedApps || [];
    if (selected.includes(appId)) {
      onChange({
        ...form,
        selectedApps: selected.filter((id) => id !== appId),
      });
    } else {
      onChange({
        ...form,
        selectedApps: [...selected, appId],
      });
    }
  };

  // Remove one selected app from list
  const removeApp = (appId) => {
    onChange({
      ...form,
      selectedApps: (form.selectedApps || []).filter((id) => id !== appId),
    });
  };

  const selectedApps = form.selectedApps || [];

  // 🧮 Mock Analytics Fetch
  const handleFetchAnalytics = async () => {
    debugger
  if (!form.publisherId || !selectedApps.length) {
    toast.error("Please select publisher and apps first.");
    return;
  }
  if (!form.fromDate || !form.toDate) {
    toast.error("Please select both From and To dates.");
    return;
  }

  setIsFetchingAnalytics(true);
  const loadingToast = toast.loading("Fetching analytics...");

  try {
    const response = await appAnalyticsService.getRevenue(
      form.publisherId,
      form.fromDate,
      form.toDate,
      form.selectedApps || [],
      form.selectedWebs || []
    );

    onChange({
      ...form,
      originalRevenue: response.totalRevenue?.toFixed(2) || 0,
    });

    toast.success("Analytics data fetched successfully!");
  } catch (error) {
    console.error("Error fetching analytics:", error);
    toast.error("Failed to fetch analytics data");
  } finally {
    toast.dismiss(loadingToast);
    setIsFetchingAnalytics(false);
  }
};

  return (
    <div className="max-h-[80vh] overflow-y-auto px-2 sm:px-4">
      <form onSubmit={onSubmit} className="space-y-5 pb-6">
        {/* Step 1: Publisher Selection */}
        <div>
          <h3 className="font-semibold text-gray-800 mb-2">Step 1: Select Publisher</h3>
          <select
            value={form.publisherId || ""}
            onChange={(e) =>
              onChange({ ...form, publisherId: e.target.value, selectedApps: [] })
            }
            className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500"
            required
          >
            <option value="">Select Publisher</option>
            {publishers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        {/* Step 2: Select Multiple Apps/Websites */}
        {form.publisherId && (
          <div>
            <h3 className="font-semibold text-gray-800 mb-2">Step 2: Choose Apps / Websites</h3>
            <div className="space-y-2 border border-gray-200 rounded-lg p-3 bg-gray-50 max-h-48 overflow-y-auto">
              {apps.filter((a) => a.publisherId == form.publisherId).length === 0 ? (
                <p className="text-gray-500 text-sm">
                  No apps or websites found for this publisher.
                </p>
              ) : (
                apps
                  .filter((a) => a.publisherId == form.publisherId)
                  .map((a) => (
                    <div key={a.id} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedApps.includes(a.id)}
                        onChange={() => handleAppSelection(a.id)}
                        id={`app-${a.id}`}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <label htmlFor={`app-${a.id}`} className="text-gray-700">
                        {a.appId}
                      </label>
                    </div>
                  ))
              )}
            </div>
          </div>
        )}

        {/* Step 3: Date Range Selection */}
        {selectedApps.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block font-medium mb-1">From Date</label>
              <input
                type="date"
                value={form.fromDate || ""}
                onChange={(e) => onChange({ ...form, fromDate: e.target.value })}
                className="w-full border border-gray-300 rounded-lg p-2"
                required
              />
            </div>
            <div>
              <label className="block font-medium mb-1">To Date</label>
              <input
                type="date"
                value={form.toDate || ""}
                onChange={(e) => onChange({ ...form, toDate: e.target.value })}
                className="w-full border border-gray-300 rounded-lg p-2"
                required
              />
            </div>
          </div>
        )}

        {/* Step 4: Fetch Analytics */}
        {form.fromDate && form.toDate && selectedApps.length > 0 && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleFetchAnalytics}
              disabled={isFetchingAnalytics}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition"
            >
              <BarChart3 className="w-4 h-4" />
              {isFetchingAnalytics ? "Fetching..." : "Fetch Analytics"}
            </button>
          </div>
        )}

        {/* Step 5: Invoice Details */}
        {form.originalRevenue > 0 && (
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 mt-2">
            <div className="flex items-center gap-2 mb-4">
              <Calculator className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-gray-800">Revenue Breakdown</h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block font-medium mb-1">Original Revenue ($)</label>
                <input
                  type="number"
                  value={form.originalRevenue || ""}
                  onChange={(e) => onChange({ ...form, originalRevenue: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg p-2"
                  placeholder="e.g., 500"
                  required
                />
              </div>

              <div>
                <label className="block font-medium mb-1">Deduction ($)</label>
                <input
                  type="number"
                  value={form.deduction || ""}
                  onChange={(e) => onChange({ ...form, deduction: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg p-2"
                  placeholder="e.g., 100"
                />
              </div>

              <div>
                <label className="block font-medium mb-1">Revenue Deduction (%)</label>
                <input
                  type="number"
                  value={form.deductionPercentage || ""}
                  onChange={(e) =>
                    onChange({
                      ...form,
                      deductionPercentage: e.target.value,
                    })
                  }
                  placeholder="e.g., 12"
                  className="w-full border border-gray-300 rounded-lg p-2"
                />
              </div>

              <div>
                <label className="block font-medium mb-1">Final Revenue ($)</label>
                <input
                  type="text"
                  value={form.finalRevenue || 0}
                  disabled
                  className="w-full border border-gray-200 bg-gray-100 rounded-lg p-2"
                />
              </div>

              <div>
                <label className="block font-medium mb-1">Deducted Amount ($)</label>
                <input
                  type="text"
                  value={form.deductedAmount || 0}
                  disabled
                  className="w-full border border-gray-200 bg-gray-100 rounded-lg p-2"
                />
              </div>

              <div>
                <label className="block font-medium mb-1">
                  Full & Final Publisher Revenue ($)
                </label>
                <input
                  type="text"
                  value={form.publisherRevenue || 0}
                  disabled
                  className="w-full border border-gray-200 bg-gray-100 rounded-lg p-2"
                />
              </div>
            </div>
          </div>
        )}

        {paymentMethods.length > 0 && (
          <div className="mt-4">
            <label className="block font-semibold text-gray-700 mb-1">
              Select Payment Method
            </label>
            <select
              className="w-full border border-gray-300 rounded-lg p-2"
              value={selectedPayment?.id || ""}
              onChange={(e) => {
                const selected = paymentMethods.find(
                  (m) => m.id === parseInt(e.target.value)
                );
                setSelectedPayment(selected);
                onChange({ ...form, bankDetails: selected });
              }}
            >
              <option value="">-- Select Payment Method --</option>
              {paymentMethods.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.method === "Bank"
                    ? `Bank - ${m.bankName}`
                    : `${m.method} - ${m.paypalEmail || m.payoneerEmail || m.btcAddress || "" || m.upiId
                    }`}
                </option>
              ))}
            </select>

            {selectedPayment && selectedPayment.method === "Bank" && (
              <div className="mt-3 p-4 border rounded-lg bg-gray-50 text-sm text-gray-700">
                <p><strong>Bank Name:</strong> {selectedPayment.bankName}</p>
                <p><strong>Account Name:</strong> {selectedPayment.accountName}</p>
                <p><strong>Account Number:</strong> {selectedPayment.accountNumber}</p>
                <p><strong>SWIFT:</strong> {selectedPayment.swiftCode}</p>
                {selectedPayment.ibanCode && (
                  <p><strong>IBAN:</strong> {selectedPayment.ibanCode}</p>
                )}
                {selectedPayment.bankAddress && (
                  <p><strong>Bank Address:</strong> {selectedPayment.bankAddress}</p>
                )}
              </div>
            )}
          </div>
        )}


        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4 sticky bottom-0 bg-white pb-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          >
            {isEditing ? "Update Invoice" : "Create Invoice"}
          </button>
        </div>
      </form>
    </div>
  );
}
